import { CurrencyView } from '../currency-view';
import { DrawerDiaLog, DrawerDiaLogTitle } from '../drawer-dialog';
import { SpanLoading } from '../fetch-status/span-loading';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { WithdrawSubmitted } from './submitted';
import { InteractCloseIcon, InteractWithdrawIcon } from '@/client/assets';
import { APP_CHAIN, IS_DEBUG, sameAddress } from '@/client/confs/app';
import { useSingerTokenBalance } from '@/client/hooks/use-signed-token-balance';
import { useTheme } from '@/client/hooks/use-theme';
import { useWallet } from '@/client/hooks/use-wallet';
import { useAppSettingStore } from '@/client/store/app-store';
import { cn, getErrorMessage } from '@/client/utils';
import { api } from '@/client/utils/api';
import { publicClient } from '@/client/utils/chain-client';
import { amountSchema } from '@/client/utils/schema';
import { assertSuccessfulReceipt } from '@/client/utils/transaction-result';
import { CheckedState } from '@radix-ui/react-checkbox';
import { useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Address, erc20Abi, isAddress, parseUnits } from 'viem';

export const WithdrawModel = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [isTrading, setIsTrading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [hash, setHash] = useState('');
  const baseToken = useAppSettingStore(state => state.baseToken);
  const { data: tokenBalances } = useSingerTokenBalance(api);
  const tokenBalance = tokenBalances?.find(item => sameAddress(item.baseTokenAddress, baseToken.address));
  const [isChecked, setIsChecked] = useState<CheckedState>(false);
  const [isMax, setIsMax] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [isForce, setIsForce] = useState(false);
  const maxAmount = Number(tokenBalance?.balance) / Math.pow(10, tokenBalance?.baseTokenDecimals ?? 0);
  const isAmountValid = amountSchema.safeParse(withdrawAmount).success && Number(withdrawAmount) <= maxAmount;
  const isAddressValid = withdrawAddress.startsWith('0x') && isAddress(withdrawAddress);
  const amount = useMemo(() => {
    if (!tokenBalance) return;
    return isMax ? BigInt(tokenBalance.balance) : parseUnits(withdrawAmount, tokenBalance.baseTokenDecimals);
  }, [isMax, tokenBalance, withdrawAmount]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const { success } = amountSchema.safeParse(input);
    if (success || input == '') {
      const decimalParts = input.split('.');
      if (decimalParts.length > 1 && decimalParts[1].length > 2) return;
      setWithdrawAmount(input);
      setIsMax(false);
    }
  };

  const { walletClient, ready: walletReady } = useWallet();

  const mounted = useRef(true);
  const latestWallet = useRef(walletClient);
  latestWallet.current = walletClient;
  const request = useRef(0);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      request.current++;
    };
  }, []);

  const handleWithdraw = async () => {
    if (!amount || !tokenBalance) return;
    // Check wallet is ready before withdraw
    if (!walletClient?.account || !walletReady) {
      toast.error(t('wallet-not-connected', 'Please connect your wallet'));
      return;
    }
    if (isTrading) return;
    const requestId = ++request.current;
    const account = walletClient.account;
    const current = () =>
      mounted.current &&
      request.current === requestId &&
      latestWallet.current?.account?.address === account.address &&
      latestWallet.current?.chain?.id === walletClient.chain?.id;
    let awaitingReceipt = false;
    try {
      setIsTrading(true);

      // Direct ERC20 transfer via writeContract
      const transferArgs = {
        address: tokenBalance.baseTokenAddress as Address,
        abi: erc20Abi,
        functionName: 'transfer' as const,
        args: [withdrawAddress as Address, amount] as const,
        account,
      };
      const transferGas = await publicClient.estimateContractGas(transferArgs);
      if (!current()) return;
      const txHash = await walletClient.writeContract({
        ...transferArgs,
        chain: walletClient.chain,
        gas: (transferGas * 130n) / 100n,
      });
      awaitingReceipt = true;
      assertSuccessfulReceipt(await publicClient.waitForTransactionReceipt({ hash: txHash }));

      if (!current()) return;
      toast.success(t('withdraw-success'));
      setIsSuccess(true);
      setHash(txHash);
    } catch (error) {
      if (!current()) return;
      const msg = getErrorMessage(error);
      if (IS_DEBUG) console.log('withdraw error', error);
      const errorStr = ((error as any)?.message || (error as any)?.shortMessage || '').toLowerCase();
      if (errorStr.includes('insufficient funds') || errorStr.includes('gas required exceeds')) {
        toast.error(t('insufficient-gas', 'Insufficient BNB for gas fees. Please top up your wallet.'));
      } else {
        toast.error(
          msg === 'transaction-reverted'
            ? t('transaction-reverted')
            : awaitingReceipt
              ? t('transaction-unknown')
              : (msg ?? t('withdraw-filed'))
        );
      }
      setIsSuccess(false);
    } finally {
      // A broadcast transfer can move the balance even when the receipt read fails:
      // refresh the header balance and the max amount now, not on the next 60 s
      // poll (also if this view is gone).
      if (awaitingReceipt) void queryClient.invalidateQueries({ queryKey: ['SingerTokenBalance'] });
      if (mounted.current && request.current === requestId) setIsTrading(false);
    }
  };

  return (
    <>
      <Button
        variant={theme == 'dark' ? 'default' : 'outline'}
        className="p-4 rounded-3 h-13 dark:border dark:border-foreground/70 font-semibold leading-[normal] w-full shadow-none"
        onClick={() => setOpen(true)}
      >
        <InteractWithdrawIcon className="fill-current" />
        {t('withdraw')}
      </Button>
      <DrawerDiaLog
        className={cn('px-4 md:p-4 bg-card w-full md:max-w-[383px] md:space-y-6 flex flex-col')}
        open={open}
        setOpen={setOpen}
      >
        {isSuccess && (
          <WithdrawSubmitted
            token={t(baseToken.translateKey)}
            amount={withdrawAmount}
            to={withdrawAddress}
            network={APP_CHAIN.name}
            txHash={hash}
            onClose={() => {
              setOpen(false);
              setIsSuccess(false);
            }}
          />
        )}
        <div className={cn('flex flex-row justify-between items-center', isSuccess && 'hidden')}>
          <DrawerDiaLogTitle>
            {t('withdraw')} {t(baseToken.translateKey)}
          </DrawerDiaLogTitle>
          <Button
            size="icon"
            variant="ghost"
            className="transition-all duration-200 hover:scale-90 focus:scale-80"
            onClick={() => setOpen(false)}
          >
            <InteractCloseIcon className="!size-6 fill-foreground/40" />
          </Button>
        </div>
        {!isSuccess && (
          <div className="space-y-9">
            <div className="space-y-4 p-2">
              <div className="flex flex-col">
                <div className="flex items-center justify-between text-sm text-foreground/70 font-medium space-y-1">
                  <Label htmlFor="withdraw-amount">{t('amount')}</Label>
                  <div className="space-x-1 flex items-center">
                    <span>{t('balance')}:</span>
                    <CurrencyView value={tokenBalance?.balance} decimal={tokenBalance?.baseTokenDecimals} />
                    <span
                      className=" hover:text-foreground hover:underline cursor-pointer"
                      onClick={() => {
                        setWithdrawAmount(maxAmount.toString());
                        setIsMax(true);
                      }}
                    >
                      {t('max')}
                    </span>
                  </div>
                </div>
                <div className="pos-relative">
                  <Input
                    id="withdraw-amount"
                    placeholder="0.00"
                    className={cn(
                      'p-4 h-14 shadow-none border border-foreground/10 focus:border-foreground/40',
                      withdrawAmount?.trim().length > 0 && !isAmountValid && 'ring-1 ring-destructive/50'
                    )}
                    value={withdrawAmount}
                    onChange={handleChange}
                  />
                  <div className="pos-absolute top-1/2 right-4 -translate-y-1/2">
                    <span>{t(baseToken.translateKey)}</span>
                  </div>
                </div>
              </div>
              <div className="space-y-1">
                <Label
                  htmlFor="withdraw-address"
                  className="flex items-center justify-between text-sm text-foreground/70 font-medium"
                >
                  {t('to')}
                </Label>
                {(isForce || !isAddressValid) && (
                  <Input
                    id="withdraw-address"
                    placeholder={`${APP_CHAIN.name} ${t('address')}`}
                    className={cn(
                      'p-4 min-h-14 shadow-none border border-foreground/10 focus:border-foreground/40',
                      withdrawAddress?.trim().length > 0 && !isAddressValid && 'ring-1 ring-destructive/50'
                    )}
                    value={withdrawAddress}
                    autoFocus={isForce}
                    onFocus={() => setIsForce(true)}
                    onBlur={() => setIsForce(false)}
                    onChange={e => setWithdrawAddress(e.target.value)}
                  />
                )}
                {!isForce && isAddressValid && (
                  <div
                    className="border border-foreground/10 p-4 min-h-14 rounded-md break-words duration-200 animate-fade-in cursor-pointer"
                    onClick={() => setIsForce(true)}
                  >
                    <span className="text-beneficial">{withdrawAddress.slice(0, 6)}</span>
                    <span>{withdrawAddress.slice(6, -6)}</span>
                    <span className="text-beneficial">{withdrawAddress.slice(-6)}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="px-2 space-x-2 flex justify-between items-start">
              <Checkbox
                id="withdraw-agreement"
                checked={isChecked}
                onCheckedChange={setIsChecked}
                className="mt-2 size-5 rounded border-2 border-foreground/30 shadow-none data-[state=checked]:border-none data-[state=checked]:bg-destructive"
              />
              <Label htmlFor="withdraw-agreement" className="text-xs font-medium leading-4.5 space-x-1 cursor-pointer">
                <span>
                  {t('i-understand-this-only-supports-less-than-b-classn', {
                    chainName: APP_CHAIN.name,
                    token: t(baseToken.translateKey),
                  })}
                </span>
                <span className="text-[#F95E5E] font-bold">
                  {t('not-bridged-token-exchange', { token: t(baseToken.translateKey) })}
                </span>
              </Label>
            </div>
            <div className="p-2">
              <Button
                size="lg"
                disabled={!isChecked || !isAddressValid || !isAmountValid || !amount || isTrading}
                className="w-full rounded-3 h-14"
                onClick={handleWithdraw}
              >
                {isTrading && <SpanLoading />}
                {t('withdraw')}
              </Button>
            </div>
          </div>
        )}
      </DrawerDiaLog>
    </>
  );
};
